<template>
  <div class="navigation-tabs" v-if="tags.length > 0">
    <button
        v-for="tag in tags" :key="tag._id"
        :class="['tab', { active: isActive(tag.slug) }]" @click="navigateTo(tag.slug)">{{ tag.title }}</button>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { defineEmits, defineProps } from 'vue';

// Define Tag interface
interface Tag {
  _id: string;
  slug: string;
  title: string;
  [key: string]: any;
}

const props = defineProps({
  tags: {
    type: Array as () => Tag[],
    required: true
  },
  activeTab: {
    type: String,
    default: 'all'
  }
});

const emit = defineEmits(['tabChanged']);
const tabName = ref(props.activeTab);

// Update local state when props change
watch(() => props.activeTab, (newTab) => {
  if (newTab !== tabName.value) {
    tabName.value = newTab;
  }
});

const isActive = (tab: string) => {
  return tabName.value === tab;
};

const navigateTo = (tab: string) => {
  if (tabName.value === tab) return; // Don't navigate if already on this tab

  tabName.value = tab;

  if (tab === 'all') {
    emit('tabChanged', '', 'all');
  } else {
    const tag = props.tags.find((t: Tag) => t.slug === tab);
    if (tag) {
      emit('tabChanged', tag._id, tag.slug);
    }
  }
};
</script>

<style scoped>
.navigation-tabs {
  display: flex;
  gap: 6px;
  flex: 1;
  justify-content: center;
}

.tab {
  border: 1px solid rgba(0, 0, 0, 0.5);
  border-radius: 100px;
  padding: 4px 16px;
  text-decoration: none;
  font-weight: 400;
  font-size: 10px;
  line-height: 1;
  vertical-align: middle;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.5);
  text-align: center;
  transition: all 0.2s ease;
  cursor: pointer;
  background-color: transparent;
}

.tab:hover,
.tab.active {
  background-color: #fff;
  border-color: #fff;
  color: rgba(0, 0, 0, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);
}

.tab.active {
  font-weight: 500;
}

@media (max-width: 768px) {
  .tab {
    justify-content: center;
  }
}
</style>
